import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Calendar, Users, Settings, LogOut, Menu, ChevronLeft, ShoppingBag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { RESERVATION_TYPES, RESERVATION_STATUS } from '../firebase/reservationService';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Estado para las notificaciones pendientes
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

  // Efecto para escuchar las solicitudes especiales pendientes
  useEffect(() => {
    // Crear una consulta para solicitudes especiales pendientes
    const specialRequestsRef = collection(db, 'reservations');
    const q = query(
      specialRequestsRef,
      where('reservationType', 'in', [RESERVATION_TYPES.WAITLIST, RESERVATION_TYPES.GROUP]),
      where('status', '==', RESERVATION_STATUS.PENDING_REVIEW)
    );
    
    // Configurar listener en tiempo real
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingRequestsCount(snapshot.docs.length);
    }, (error) => {
      console.error("Error al obtener solicitudes pendientes:", error);
    });
    
    // Limpiar listener cuando el componente se desmonte
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Definición de elementos de menú, incluyendo la ruta de solicitudes especiales
  const menuItems = [
    { path: '/admin', icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
    { path: '/admin/reservas', icon: <Calendar size={20} />, label: 'Reservas' },
    { 
      path: '/admin/solicitudes-especiales', 
      icon: <Users size={20} />, 
      label: 'Solicitudes',
      badge: pendingRequestsCount > 0 // Indicador de notificación para este elemento
    },
    { path: '/admin/mesas', icon: <Users size={20} />, label: 'Mesas' },
    { path: '/admin/menu', icon: <Menu size={20} />, label: 'Carta' },
    { path: '/admin/pedidos', icon: <ShoppingBag size={20} />, label: 'Pedidos' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white shadow-md fixed h-full transition-all duration-300`}>
        <div className="p-4 border-b flex justify-between items-center">
          {!sidebarCollapsed && <h1 className="text-xl font-bold text-gray-800">Panel Admin</h1>}
          <button 
            onClick={toggleSidebar}
            className="text-gray-500 hover:text-gray-700"
          >
            {sidebarCollapsed ? (
              <Menu size={20} />
            ) : (
              <ChevronLeft size={20} />
            )}
          </button>
        </div>
        <nav className="mt-4">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-2 px-4 py-3 text-gray-700 hover:bg-gray-50 relative
                ${location.pathname === item.path ? 'bg-gray-100 border-r-4 border-blue-500' : ''}
                ${sidebarCollapsed ? 'justify-center' : ''}`}
              title={sidebarCollapsed ? item.label : ""}
            >
              {item.icon}
              {!sidebarCollapsed && <span>{item.label}</span>}
              
              {/* Indicador de notificación */}
              {item.badge && (
                <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-xs font-medium text-white">
                  {pendingRequestsCount}
                </span>
              )}
            </Link>
          ))}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-2 px-4 py-3 text-red-600 hover:bg-red-50 w-full 
              ${sidebarCollapsed ? 'justify-center' : 'text-left'}`}
            title={sidebarCollapsed ? "Cerrar Sesión" : ""}
          >
            <LogOut size={20} />
            {!sidebarCollapsed && <span>Cerrar Sesión</span>}
          </button>
        </nav>
      </aside>

      {/* Main content - ajustar el margen según el estado del sidebar */}
      <main className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} p-8 transition-all duration-300`}>
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;